import axios from 'axios';
import * as Sentry from '@sentry/react';

const LOCAL_API_URL = 'http://localhost:5000';
const PRODUCTION_API_URL = 'https://api.stagecrew.app';
const configuredApiUrl = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_PRODUCTION_API_URL
  : process.env.REACT_APP_API_URL;

const defaultApiUrl = process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test'
  ? LOCAL_API_URL
  : PRODUCTION_API_URL;

export const API_URL = (configuredApiUrl || defaultApiUrl).replace(/\/+$/, '');

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getBearerToken = (headers: any): string | null => {
  const authorization = typeof headers?.get === 'function'
    ? headers.get('Authorization')
    : headers?.Authorization || headers?.authorization;

  if (typeof authorization !== 'string') return null;
  const match = authorization.match(/^Bearer\s+(.+)$/i);
  return match?.[1] || null;
};

function safeApiPath(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0) return null;
  try {
    return new URL(value, API_URL).pathname;
  } catch {
    return value.split(/[?#]/)[0] || null;
  }
}

// Attach auth token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const handleApiResponseError = async (error: any) => {
  const originalRequest = error.config;

  if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
    const requestAccessToken = getBearerToken(originalRequest.headers);
    const currentAccessToken = localStorage.getItem('accessToken');

    // The response belongs to an older account/session. Never use the current
    // account's refresh token to replay that old request.
    if (!requestAccessToken || requestAccessToken !== currentAccessToken) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    const refreshToken = localStorage.getItem('refreshToken');

    if (refreshToken) {
      try {
        const res = await axios.post(`${API_URL}/api/auth/refresh`, { refreshToken });

        // Login/logout may have replaced the session while refresh was in
        // flight. In that case, leave the new session untouched and do not
        // replay the old request.
        if (
          localStorage.getItem('accessToken') !== requestAccessToken
          || localStorage.getItem('refreshToken') !== refreshToken
        ) {
          return Promise.reject(error);
        }

        const { accessToken } = res.data;
        localStorage.setItem('accessToken', accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        // A failed refresh may only clear the same session that initiated it.
        // Never clear or redirect a replacement session.
        if (
          localStorage.getItem('accessToken') === requestAccessToken
          && localStorage.getItem('refreshToken') === refreshToken
        ) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
        }
      }
    }
  }

  // Report 5xx errors to Sentry (gives client-side context for server failures)
  if (error.response?.status >= 500) {
    const safeError = new Error('StageCrew API request failed');
    safeError.name = 'ApiResponseError';
    Sentry.captureException(safeError, {
      extra: {
        path: safeApiPath(error.config?.url),
        method: error.config?.method,
        status: error.response.status,
      },
    });
  }

  return Promise.reject(error);
};

// Handle 401 responses — attempt token refresh
api.interceptors.response.use(
  (response) => response,
  handleApiResponseError,
);

export default api;
